import { html, render, type TemplateResult } from 'lit-html'
import type { Content, FormatArgs, api as coreApi } from '@guild/core'

import { createApi, type ApiClient } from './api.ts'
import { loadContent } from './content/index.ts'
import {
  clearDay,
  COURSE_STATE_KEYS,
  COURSE_STEP_KEYS,
  courseOf,
  dayStore,
  loadDay,
  routeForStep,
} from './day.ts'
import { communityLink } from './community.ts'
import { clearInbox, inboxStore, loadInbox, unopenedCount } from './inbox.ts'
import { clearLetterSent, letterSentStore } from './letter-sent.ts'
import { clearNotices, noticeStore, unopenedNotices } from './notices.ts'
import type { Practice } from './practice.ts'
import {
  NAV_KEYS,
  NAV_KEYS_SHORT,
  NAV_SCREENS,
  parseRoute,
  routePath,
  type Route,
} from './route.ts'
import type { Screen } from './screen.ts'
import { islandScreen } from './screens/island.ts'
import { letterScreen } from './screens/letter.ts'
import { postScreen } from './screens/post.ts'
import { atlasScreen } from './screens/atlas.ts'
import { dayEndScreen } from './screens/day-end.ts'
import { authScreen } from './screens/auth.ts'
import { codeScreen } from './screens/code.ts'
import { errorText, t } from './text.ts'
import { tickEveryMinute, tideLine } from './tide.ts'

/**
 * The shell: who is signed in, which locale they read, which theme they set,
 * and which screen is on show.
 *
 * It owns nothing about the game. It holds the session, the content view for
 * the reader's locale (Q12.5), the route and a render loop; every screen
 * beneath it is one small store and one `render(state)`.
 *
 * The locale comes from the player's own settings through `GET /api/auth/me`
 * and falls back to the source locale for anyone not signed in (#84). A locale
 * picker appears only when more than one locale is served, which until release
 * is never.
 */

export type Theme = 'ink' | 'parchment'
export const THEMES: readonly Theme[] = ['ink', 'parchment']

/** Four steps of text size (#105), each a multiplier on every size token. */
export type TextSize = 'small' | 'regular' | 'large' | 'larger'
export const TEXT_SIZES: readonly TextSize[] = ['small', 'regular', 'large', 'larger']
const TEXT_SCALE: Readonly<Record<TextSize, string>> = {
  small: '0.9',
  regular: '1',
  large: '1.15',
  larger: '1.3',
}
const TEXT_SIZE_KEYS: Readonly<Record<TextSize, string>> = {
  small: 'textSizeSmall',
  regular: 'textSizeRegular',
  large: 'textSizeLarge',
  larger: 'textSizeLarger',
}

export type App = {
  /**
   * The client every screen asks. The network client, or -- on the practice
   * shore -- the local one, which answers from memory (#134). A screen never
   * knows which it holds.
   */
  api: ApiClient
  /** The practice shore, while the player is on it; null on the real islands. */
  practice: Practice | null
  content: Content
  locale: string
  user: coreApi.ApiMe | null
  route: Route
  theme: Theme
  textSize: TextSize
  /** Whether the desk is open. */
  desk: boolean
  /**
   * What went wrong in the shell itself, rather than on a screen.
   *
   * There is one thing the shell does on its own behalf -- signing out -- and
   * a refusal of it has nowhere else to be said (#109).
   */
  problem: string | null
  /** True when the reader asked for less movement. Motion is skipped, not faked. */
  reducedMotion: boolean
  /**
   * True below 720 px, where the header is a strip that scrolls (#122) and the
   * five keys are drawn short (#128). The one thing the shell draws
   * differently by width that CSS could not do alone: a label is content, and
   * two labels in the markup for one key would be read twice.
   */
  narrow: boolean
  t(key: string, args?: FormatArgs): string
  go(route: Route): void
  /** Ask for a re-render; several asks in one turn produce one render. */
  refresh(): void
  /** Take the session a sign-in, a sign-out or a 401 established. */
  setSession(user: coreApi.ApiMe | null): Promise<void>
  setTheme(theme: Theme): void
  setTextSize(size: TextSize): void
  openSettings(): void
  closeSettings(): void
  /** Onto the practice shore: nothing kept, nothing sent, nothing counted (#134). */
  enterPractice(): Promise<void>
  /** Back to the real island, with nothing changed. */
  leavePractice(): Promise<void>
}

const THEME_KEY = 'guild.theme'
const TEXT_SIZE_KEY = 'guild.text-size'

/** Reading the theme is best-effort: a browser that refuses storage still plays. */
function storedTheme(): Theme {
  try {
    return window.localStorage.getItem(THEME_KEY) === 'parchment' ? 'parchment' : 'ink'
  } catch {
    return 'ink'
  }
}

function rememberTheme(theme: Theme): void {
  remember(THEME_KEY, theme)
}

function storedTextSize(): TextSize {
  try {
    const stored = window.localStorage.getItem(TEXT_SIZE_KEY)
    return TEXT_SIZES.find((size) => size === stored) ?? 'regular'
  } catch {
    return 'regular'
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // A setting that cannot be remembered is still a setting for this visit.
  }
}

const SCREENS: readonly Screen[] = [
  islandScreen,
  letterScreen,
  postScreen,
  atlasScreen,
  dayEndScreen,
  authScreen,
  codeScreen,
]

/**
 * Put every store back as a fresh session finds it (#115).
 *
 * On both edges, not only on sign-out. A store lives as long as its module, so
 * a browser that goes from one account straight to another -- which never
 * passes through a signed-out state -- would otherwise show the next player
 * the last one's map, position, guidance and post until their own arrived.
 *
 * Resetting also retires whatever is in flight: a screen's `enter` and the two
 * shared loads all check whether they are still the current one before they
 * write, so a response addressed to the player who left is dropped.
 */
export function forgetPlayer(): void {
  clearInbox()
  clearLetterSent()
  clearNotices()
  clearDay()
  for (const screen of SCREENS) screen.reset()
}

/**
 * Onto the practice shore (#134).
 *
 * Every store is put back as a fresh session finds it, exactly as a sign-in
 * does (#115): the practice begins with nothing of the real island in it, and
 * the real island gets nothing of the practice back. The client is swapped
 * under the screens, which go on asking the same questions.
 */
export async function enterPractice(app: App): Promise<void> {
  if (app.practice !== null || app.user === null) return
  // Its own chunk: the shore brings the island generator and the rest of the
  // rules into the browser, and a player who goes straight to today's island
  // never loads them.
  const { createPractice } = await import('./practice.ts')
  if (app.practice !== null || app.user === null) return
  const practice = createPractice({
    real: app.api,
    user: app.user,
    content: app.content,
    locale: app.locale,
    // The post comes at the seal rather than at midnight, and the mark beside
    // `[ The Post ]` is told the way a new day tells it (#116).
    onPost: () => void loadInbox(app),
  })
  forgetPlayer()
  app.practice = practice
  app.api = practice.api
  app.desk = false
  app.problem = null
  app.refresh()
  app.go({ screen: 'island', day: null })
  await loadInbox(app)
}

/**
 * Back to the real island, with nothing changed.
 *
 * The practice state lives in the client that is dropped here and nowhere
 * else -- not in a store, not in storage -- so leaving is forgetting.
 */
export async function leavePractice(app: App): Promise<void> {
  const practice = app.practice
  if (practice === null) return
  app.api = practice.real
  app.practice = null
  app.desk = false
  forgetPlayer()
  app.refresh()
  app.go({ screen: 'island', day: null })
  await loadInbox(app)
}

export async function createApp(root: HTMLElement): Promise<App> {
  const client = createApi()
  const session = await client.me()
  const user = session.ok ? session.value.user : null
  const content = await loadContent(user?.locale)

  let scheduled = false

  const app: App = {
    api: client,
    practice: null,
    content,
    locale: content.resolve(user?.locale),
    user,
    route: parseRoute(window.location.hash),
    theme: storedTheme(),
    textSize: storedTextSize(),
    desk: false,
    problem: null,
    reducedMotion:
      typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches,
    narrow: typeof window.matchMedia === 'function' && window.matchMedia(NARROW).matches,

    t: (key, args) => t(app, key, args),

    go(route) {
      const path = routePath(route)
      if (window.location.hash === path) {
        void enter(app, route)
        return
      }
      window.location.hash = path
    },

    refresh() {
      if (scheduled) return
      scheduled = true
      queueMicrotask(() => {
        scheduled = false
        render(shell(app), root)
        keepActiveKeyInView(root)
      })
    },

    async setSession(next) {
      // A session that ends or changes ends the practice with it: the shore
      // belongs to the player who walked onto it.
      if (app.practice !== null) {
        app.api = app.practice.real
        app.practice = null
      }
      app.user = next
      app.problem = null
      forgetPlayer()
      if (next !== null) void loadDay(app)
      const locale = next?.locale
      if (app.locale !== app.content.resolve(locale)) {
        app.content = await loadContent(locale)
        app.locale = app.content.resolve(locale)
      }
      applyDocument(app)
      app.refresh()
    },

    setTheme(theme) {
      app.theme = theme
      rememberTheme(theme)
      applyDocument(app)
      app.refresh()
    },

    setTextSize(size) {
      app.textSize = size
      remember(TEXT_SIZE_KEY, size)
      applyDocument(app)
      app.refresh()
    },

    openSettings() {
      deskOpener = document.activeElement
      app.desk = true
      app.refresh()
      window.setTimeout(() => {
        const close = document.querySelector('.desk-close')
        if (close instanceof HTMLElement) close.focus()
      }, 0)
    },

    closeSettings() {
      if (!app.desk) return
      app.desk = false
      app.refresh()
      const back = deskOpener
      deskOpener = null
      window.setTimeout(() => {
        if (back instanceof HTMLElement && back.isConnected) back.focus()
      }, 0)
    },

    enterPractice: () => enterPractice(app),
    leavePractice: () => leavePractice(app),
  }

  for (const screen of SCREENS) screen.watch(() => app.refresh())
  // The post is shared between the navigation's mark and the post screen (#103);
  // whether today's letter has gone is shared by the island and the sheet (#102).
  inboxStore.watch(() => app.refresh())
  letterSentStore.watch(() => app.refresh())
  // And the notices from the Rolls, which the mark and the course read too (Q32.2).
  noticeStore.watch(() => app.refresh())
  // The day's course is drawn from the day store, the inbox and the letter
  // (#135); the day is fetched once on boot, and the post with it.
  dayStore.watch(() => app.refresh())
  if (user !== null) void loadDay(app)

  // Asking once at boot would hold a reader to whatever the setting was when
  // they opened the tab, and it is a setting people change while looking at
  // the thing that made them want to (#118).
  watchReducedMotion(app)
  watchWidth(app)
  // The count under the course moves once a minute (#139). A redraw and
  // nothing more: the clock decides no rule (Q17.1).
  tickEveryMinute(() => {
    if (app.user !== null && app.practice === null) app.refresh()
  })

  window.addEventListener('hashchange', () => {
    // Always enter, never only when the route reads as different: signing in
    // moves the hash from nothing to `#/island`, which parses to the route the
    // app already had, and the screen would never load its own state.
    const next = parseRoute(window.location.hash)
    app.route = next
    app.refresh()
    void enter(app, next)
  })

  window.addEventListener('keydown', (event) => {
    // Escape closes the desk before it reaches the screen behind it.
    if (app.desk && event.key === 'Escape') {
      event.preventDefault()
      app.closeSettings()
      return
    }
    if (app.desk) return
    screenFor(app).keydown(event, app)
  })

  applyDocument(app)
  app.refresh()
  await enter(app, app.route)
  return app
}

/**
 * Keep the key for the screen you are on inside the navigation strip (#122).
 *
 * At narrow widths the five keys are one row that scrolls sideways, and the
 * last of them is past the right edge of a 360 px phone. Nothing in CSS can
 * scroll, so the strip is nudged after each render -- by the least that brings
 * the key into view, so a reader who scrolled the strip themselves keeps their
 * place, and instantly, because a header that slides about is motion this game
 * does not have and `prefers-reduced-motion` would have to switch off.
 *
 * A no-op wherever the strip is not scrolling, which is every desktop width.
 */
function keepActiveKeyInView(root: HTMLElement): void {
  const nav = root.querySelector('.nav')
  const here = root.querySelector('.verb-here')
  if (!(nav instanceof HTMLElement) || !(here instanceof HTMLElement)) return
  if (nav.scrollWidth <= nav.clientWidth) return
  const strip = nav.getBoundingClientRect()
  const key = here.getBoundingClientRect()
  if (key.left < strip.left) nav.scrollLeft -= strip.left - key.left
  else if (key.right > strip.right) nav.scrollLeft += key.right - strip.right
}

/** The query that says the reader wants less movement, when there is one. */
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

/** The width below which the header is a strip; the same figure as `app.css`. */
const NARROW = '(max-width: 720px)'

/** Follow the width across the strip's breakpoint, so a turned phone redraws (#128). */
function watchWidth(app: App): void {
  if (typeof window.matchMedia !== 'function') return
  const query = window.matchMedia(NARROW)
  if (typeof query.addEventListener !== 'function') return
  query.addEventListener('change', (event) => {
    app.narrow = event.matches
    app.refresh()
  })
}

/** Follow `prefers-reduced-motion` for as long as the tab is open (#118). */
function watchReducedMotion(app: App): void {
  if (typeof window.matchMedia !== 'function') return
  const query = window.matchMedia(REDUCED_MOTION)
  if (typeof query.addEventListener !== 'function') return
  query.addEventListener('change', (event) => {
    app.reducedMotion = event.matches
    app.refresh()
  })
}

/** The theme, the language, and the document's title, which is content too. */
function applyDocument(app: App): void {
  document.documentElement.dataset['theme'] = app.theme
  document.documentElement.style.setProperty('--text-scale', TEXT_SCALE[app.textSize])
  document.documentElement.lang = app.locale
  document.title = app.t('guildName')
}

/**
 * Signed out, every route is the landing, with the register behind it (#136)
 * -- except the code of the Guild, which the landing's foot links to and
 * which anybody may read (#68).
 */
function screenFor(app: App): Screen {
  const wanted = app.user === null && app.route.screen !== 'code' ? 'auth' : app.route.screen
  return SCREENS.find((screen) => screen.id === wanted) ?? authScreen
}

async function enter(app: App, route: Route): Promise<void> {
  await screenFor(app).enter(app, route)
}

// --- the frame -------------------------------------------------------------

function routeFor(screen: (typeof NAV_SCREENS)[number]): Route {
  switch (screen) {
    case 'island':
      return { screen: 'island', day: null }
    case 'post':
      return { screen: 'post', publicId: null }
    case 'day':
      return { screen: 'day', day: null }
    default:
      return { screen }
  }
}

/**
 * A mark beside the post when something is sealed (#103).
 *
 * One glyph, titled with what it means, and nothing that looks like a badge:
 * the count is in the title rather than on the screen because the number is
 * not the point -- that a letter is there is. A notice from the Rolls counts
 * too (Q32.2), and is what the title names when only notices wait.
 */
function waiting(app: App): TemplateResult | null {
  const n = unopenedCount()
  const notices = unopenedNotices()
  if (n === 0 && notices === 0) return null
  const said = n === 0 ? app.t('noticeWaiting', { n: notices }) : app.t('postWaiting', { n })
  return html`<span class="waiting" role="img" aria-label=${said} title=${said}>✦</span>`
}

/**
 * The five keys. On a desk, the long names in brackets; on a phone, the short
 * names with the brackets dropped, because the key is already a drawn box and
 * at that width two characters a key is a key off the screen (#128, Q24.1).
 */
function navigation(app: App): TemplateResult {
  const here = app.route.screen
  const keys = app.narrow ? NAV_KEYS_SHORT : NAV_KEYS
  return html`<nav class="nav" aria-label=${app.t('guildName')}>
    ${NAV_SCREENS.map(
      (screen) =>
        html`<button
          class="verb ${screen === here ? 'verb-here' : ''}"
          aria-current=${screen === here ? 'page' : 'false'}
          @click=${() => app.go(routeFor(screen))}
        >
          ${app.narrow ? null : '[ '}${app.t(keys[screen])}
          ${screen === 'post' ? waiting(app) : null}${app.narrow ? null : ' ]'}
        </button>`,
    )}
  </nav>`
}

/**
 * The day's course (#135, Q27.1): the four steps of a day, in order, each a
 * key to its screen with a state word after it, the current one in the
 * accent. The first two read "not today" for a player who did not walk
 * yesterday's rising day, rather than being left out: a course that is
 * always the same four steps is a course a new player learns on the first
 * day, and a strip that changes shape between days is not. When nothing is
 * left to do the strip is one line.
 *
 * Under the header on every signed-in screen, and not on the practice
 * shore, which is nobody's day (#134), nor over the welcome, which is read
 * before the day begins (#137), nor over the code of the Guild, which is
 * not a step of it (#68). Nothing here before the day store has answered,
 * rather than four keys that then change under the eye.
 *
 * At the end of it, when the tide turns (#139, Q30.1): the instant the
 * server gave, in the viewer's own time, and how long is left. On the
 * island going under, the line says so instead -- the same instant, since
 * today begins sinking as yesterday finalises, but it is yesterday's the
 * screen is showing.
 */
function course(app: App): TemplateResult | null {
  if (app.user === null || app.practice !== null) return null
  const shown = screenFor(app).id
  if (shown === 'auth' || shown === 'code') return null
  const today = courseOf()
  if (today === null) return null
  const underfoot =
    shown === 'island' &&
    app.route.screen === 'island' &&
    app.route.day !== null &&
    app.route.day === today.yesterday
  const tide = underfoot
    ? tideLine(app, 'tideGoesUnderAt', today.goesUnderAt)
    : tideLine(app, 'tideTurnsAt', today.turnsAt)
  if (today.complete) {
    return html`<div class="course">
      <p class="course-complete" role="status">${app.t('courseComplete')}</p>
      ${tide}
    </div>`
  }
  return html`<nav class="course" aria-label=${app.t('courseHeading')}>
    <ol class="course-steps">
      ${today.steps.map(
        ({ step, state }) =>
          html`<li>
            <button
              class="course-step course-${state === 'notToday' ? 'not-today' : state}"
              aria-current=${state === 'now' ? 'step' : 'false'}
              @click=${() => app.go(routeForStep(step, today))}
            >
              <span class="course-name">${app.t(COURSE_STEP_KEYS[step])}</span>
              <span class="course-state">${app.t(COURSE_STATE_KEYS[state])}</span>
            </button>
          </li>`,
      )}
    </ol>
    ${tide}
  </nav>`
}

/**
 * Signing out, which only counts once the server says so (#109).
 *
 * A logout that never arrived leaves the cookie valid, so clearing the client
 * would show the register over a session that is still open -- and hand the
 * account to whoever picks a shared browser up next. A refusal keeps the
 * player where they are and says why.
 */
async function leave(app: App): Promise<void> {
  // Through the real client, whatever shore the player is on: the practice
  // client knows no session to end, and leaving the shore to sign out would
  // land them on today's island on the way past.
  const left = await (app.practice?.real ?? app.api).logout()
  if (!left.ok) {
    app.problem = errorText(app, left)
    app.refresh()
    return
  }
  await app.setSession(null)
}

function settings(app: App): TemplateResult {
  return html`<div class="settings">
    <button
      class="quiet"
      aria-expanded=${app.desk ? 'true' : 'false'}
      @click=${() => (app.desk ? app.closeSettings() : app.openSettings())}
    >
      ${app.t('settingsOpen')}
    </button>
    ${
      app.user === null
        ? null
        : html`<button class="quiet" @click=${() => void leave(app)}>${app.t('authLeave')}</button>`
    }
  </div>`
}

/** Where the focus was before the desk took it, so it can be given back. */
let deskOpener: Element | null = null

/**
 * The desk: the theme, the text size, and the locale when there is one to pick
 * (#105, Q12.5).
 *
 * Both settings live in `localStorage` rather than in the account, because
 * nothing writes a setting back to the server yet (Q18.5). A browser that
 * refuses storage still plays; it simply chooses again next visit.
 */
function settingsPanel(app: App): TemplateResult {
  const served = app.content.served
  return html`<div class="desk" role="dialog" aria-label=${app.t('settingsHeading')}>
    <p class="label">${app.t('settingsHeading')}</p>

    <p class="row">
      <span class="label">${app.t('settingsTheme')}</span>
      ${THEMES.map(
        (theme) =>
          html`<button
            class="quiet ${theme === app.theme ? 'chosen' : ''}"
            aria-pressed=${theme === app.theme ? 'true' : 'false'}
            @click=${() => app.setTheme(theme)}
          >
            ${app.t(theme === 'ink' ? 'themeInk' : 'themeParchment')}
          </button>`,
      )}
    </p>

    <p class="row">
      <span class="label">${app.t('settingsTextSize')}</span>
      ${TEXT_SIZES.map(
        (size) =>
          html`<button
            class="quiet ${size === app.textSize ? 'chosen' : ''}"
            aria-pressed=${size === app.textSize ? 'true' : 'false'}
            @click=${() => app.setTextSize(size)}
          >
            ${app.t(TEXT_SIZE_KEYS[size])}
          </button>`,
      )}
    </p>

    ${
      served.length > 1
        ? html`<p class="row">
            <label class="label"
              >${app.t('settingsLocale')}
              <select
                @change=${(event: Event) => {
                  const locale = (event.target as HTMLSelectElement).value
                  void app.setSession(app.user === null ? null : { ...app.user, locale })
                }}
              >
                ${served.map(
                  (locale) =>
                    html`<option value=${locale} ?selected=${locale === app.locale}>
                      ${locale}
                    </option>`,
                )}
              </select>
            </label>
          </p>`
        : null
    }

    <!-- The Guild's common room, one more row under the language (#138). -->
    <p class="row">${communityLink(app)}</p>
    ${
      // The practice shore, from the desk, for anyone who skipped it or wants
      // it again (#134). Not while on it: the banner carries the way off.
      app.user === null || app.practice !== null
        ? null
        : html`<p class="row">
            <button
              class="verb"
              @click=${() => {
                app.closeSettings()
                void app.enterPractice()
              }}
            >
              [ ${app.t('practiceFromDesk')} ]
            </button>
          </p>`
    }

    <!-- The code of the Guild, from the desk as from the landing's foot (#68). -->
    <p class="row">
      <button
        class="quiet desk-code"
        @click=${() => {
          app.closeSettings()
          app.go({ screen: 'code' })
        }}
      >
        ${app.t('navCodeOfConduct')}
      </button>
    </p>

    <button class="verb verb-end desk-close" @click=${() => app.closeSettings()}>
      [ ${app.t('settingsClose')} ]
    </button>
  </div>`
}

/**
 * The banner over the practice shore (#134): that nothing here is kept, and
 * the way off it. Always on show while the practice runs, whatever screen is
 * under it, so a player is never in doubt which island they are on.
 */
function practiceBanner(app: App): TemplateResult | null {
  if (app.practice === null) return null
  return html`<div class="practice-banner" role="status">
    <span>${app.t('practiceBanner')}</span>
    <button class="verb practice-leave" @click=${() => void app.leavePractice()}>
      [ ${app.t('practiceLeave')} ]
    </button>
  </div>`
}

function shell(app: App): TemplateResult {
  return html`<div class="page">
    <header class="guild-header">
      <div class="guild-mark">${app.t('guildName')}</div>
      ${app.user === null ? null : navigation(app)}${settings(app)}
    </header>
    ${course(app)} ${practiceBanner(app)}
    ${app.problem === null ? null : html`<p class="problem" role="alert">${app.problem}</p>`}
    ${app.desk ? settingsPanel(app) : null}
    <main class="page-body" id="main">${screenFor(app).render(app)}</main>
  </div>`
}

export { shell }
